/**
 * Tableau de bord — page d'accueil.
 *
 * Il répond à trois questions, dans cet ordre : qu'est-ce qui ne va pas, où en
 * est l'exercice, où va la trajectoire. Rien de plus : un tableau de bord qui
 * exige d'être lu en entier n'en est pas un.
 */

import {
  dateFr,
  entier,
  euros,
  eurosSigne,
  keurosValeur,
  moisCourt,
  nombre,
  pourcent,
} from '@scc/core';
import type { Charge } from '../api.js';
import { h } from '../dom.js';
import { TEINTES, graphiqueAnneau, graphiqueLignes, legende } from '../graphiques.js';
import { barre, insight, noteLecture, regleAccent } from '../composants.js';
import { listeAlertes } from './alertes.js';
import { demanderExercice } from './exercice-compare.js';

/** Tuile portant un chiffre clé et sa lecture. */
function tuile(
  titre: string,
  valeur: string,
  unite: string | null,
  lecture: string,
  jauge?: { part: number; teinte: string; gauche: string; droite: string },
): HTMLElement {
  return h(
    'div',
    { classe: 'tuile' },
    h('h3', {}, titre),
    h(
      'p',
      { classe: 'chiffre-cle' },
      valeur,
      unite ? h('span', { classe: 'unite' }, ` ${unite}`) : null,
    ),
    h('p', { classe: 'sous-chiffre' }, lecture),
    jauge
      ? h(
          'div',
          { classe: 'jauge' },
          h(
            'div',
            {
              classe: 'jauge-piste',
              role: 'progressbar',
              'aria-valuenow': Math.round(jauge.part * 100),
              'aria-valuemin': 0,
              'aria-valuemax': 100,
              'aria-label': `${titre} : ${pourcent(jauge.part, 0)}`,
            },
            h('div', {
              classe: 'jauge-part',
              '--part': `${Math.max(1, Math.min(100, jauge.part * 100)).toFixed(1)}%`,
              style: null,
            }),
          ),
          h(
            'div',
            { classe: 'jauge-reperes' },
            h('span', {}, jauge.gauche),
            h('span', {}, jauge.droite),
          ),
        )
      : null,
  );
}

export function vueTableauBord(d: Charge, allerA: (page: string) => void): HTMLElement {
  const ex = d.execution;
  const pr = d.projection;
  const ch = d.chainage;
  const an = d.meta.exerciceCourant;
  const sc3 = d.trajectoires.find((t) => t.cle === 'sc3');
  const annees = sc3?.annees.map((a) => a.annee) ?? [];

  // Teinte de la jauge d'exécution : verte quand produits et charges avancent
  // du même pas, ambre quand les charges prennent de l'avance.
  const ecartRythme = d.tauxExecution.produits - d.tauxExecution.charges;
  const teinteRythme = ecartRythme > -0.1 ? 'color-green' : ecartRythme > -0.25 ? 'color-amber' : 'color-danger';

  const graphique = h(
    'div',
    {},
    graphiqueLignes(
      [d.cloture.exercice, ...annees],
      d.trajectoires
        .filter((t) => t.cle !== 'sim')
        .map((t) => ({
          nom: t.libelle,
          couleur:
            t.cle === 'sc1' ? TEINTES.slate : t.cle === 'sc2' ? TEINTES.green : TEINTES.amber,
          valeurs: [ch.fondsPropresCloture, ...t.annees.map((a) => a.reserve)],
          epaisseur: t.cle === 'sc3' ? 3 : 2,
        })),
      {
        bande: [ch.seuilSecurite, ...(sc3?.annees.map((a) => a.seuil) ?? [])],
        nomBande: 'Seuil de sécurité',
        depuisZero: true,
        titre: 'Trajectoire des réserves',
        enteteAbscisse: 'Exercice',
      },
    ),
    legende([
      ...d.trajectoires
        .filter((t) => t.cle !== 'sim')
        .map((t) => ({
          nom: t.libelle,
          couleur:
            t.cle === 'sc1' ? TEINTES.slate : t.cle === 'sc2' ? TEINTES.green : TEINTES.amber,
        })),
      { nom: 'Seuil de sécurité', couleur: TEINTES.danger },
    ]),
  );

  // Trésorerie mensuelle constatée : le solde réel, pas une projection.
  const graphiqueTresorerie = graphiqueLignes(
    d.tresorerie.mensuelle.map((p) => moisCourt(p.mois)),
    [
      {
        nom: 'Solde bancaire',
        couleur: TEINTES.blue,
        valeurs: d.tresorerie.mensuelle.map((p) => p.solde),
      },
    ],
    {
      titre: `Trésorerie mensuelle constatée ${an}`,
      enteteAbscisse: 'Mois',
      unite: 'euros',
      bande: d.tresorerie.mensuelle.map(
        () => d.tresorerie.parScenario[0]?.passage[0]?.plancher ?? 0,
      ),
      nomBande: 'Plancher de trésorerie',
      depuisZero: true,
    },
  );

  // ── Le verdict, en une phrase ───────────────────────────────────────────
  //
  // Trois signaux suffisent à dire si la trajectoire tient : l'écart du
  // résultat projeté au prévisionnel, la couverture du seuil d'alerte, et
  // l'échéance de décision la plus proche. Le reste de la page les détaille.
  const av = d.adherents.avancement;
  const proche = d.veille.plusProche;
  const sousSeuilPeriode = d.veille.lignes.reduce((s, l) => s + l.nbSousSeuil, 0);

  const negatif =
    ch.multipleSeuil < 1.5 || d.veille.gravite === 'alerte' || av.verdict === 'en_retard';
  const vigilance = d.veille.gravite === 'vigilance' || pr.ecartResultat < 0 || sousSeuilPeriode > 0;

  const verdict = insight(
    negatif ? 'negative' : vigilance ? 'warning' : 'positive',
    negatif ? '⚠' : vigilance ? '◷' : '✓',
    `**${
      negatif
        ? 'Trajectoire à redresser.'
        : vigilance
          ? 'Trajectoire tenable, sous surveillance.'
          : 'Trajectoire positive.'
    }** ` +
      `L'exercice ${an} se projette à ${eurosSigne(pr.resultatProjete)}, ` +
      `${pr.ecartResultat >= 0 ? 'soit mieux que' : 'soit'} ${eurosSigne(pr.ecartResultat)} par rapport au ` +
      `prévisionnel. La réserve attendue à la clôture, ${euros(ch.reserveAttendue)}, couvre ` +
      `${nombre(ch.multipleSeuil, 2)} fois le seuil d'alerte. ` +
      (proche?.echeanceDecision
        ? `Le scénario « ${proche.libelle} » passe sous ce seuil en ${proche.exerciceCritique ?? ''}` +
          `${proche.extrapole ? ' par prolongation de son dernier résultat' : ''} : une décision serait ` +
          `attendue avant le ${dateFr(proche.echeanceDecision)}, dans ${entier(proche.moisRestants ?? 0)} mois.`
        : `Aucun scénario ne passe sous ce seuil sur la période projetée.`) +
      ` Côté cotisations, l'assiette d'appel est ${
        av.verdict === 'conforme'
          ? 'conforme à la cible'
          : av.verdict === 'en_avance'
            ? 'au-dessus de la cible'
            : av.verdict === 'en_retard'
              ? 'insuffisante'
              : 'indéterminée'
      }.`,
  );

  // ── Les ressources, poste par poste ─────────────────────────────────────
  const ressources = d.execution.produitsParPoste
    .filter((p) => p.budgete !== null && p.budgete > 0)
    .sort((a, b) => (b.budgete ?? 0) - (a.budgete ?? 0));

  const barresRessources = h(
    'table',
    { classe: 'bar-table encadre-tableau' },
    h('caption', { classe: 'invisible' }, `Encaissements ${an} par ressource`),
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { scope: 'col' }, 'Ressource'),
        h('th', { scope: 'col' }, 'Part déjà encaissée'),
        h('th', { scope: 'col' }, 'Encaissé'),
        h('th', { scope: 'col' }, 'Budgété'),
      ),
    ),
    h(
      'tbody',
      {},
      ...ressources.map((p) => {
        const part = p.tauxRealisation ?? 0;
        return h(
          'tr',
          {},
          h('th', { scope: 'row' }, p.libelle),
          h(
            'td',
            {},
            barre(
              part,
              part >= 0.7 ? 'color-green' : part >= 0.25 ? 'color-amber' : 'color-danger',
              pourcent(part, 0),
            ),
          ),
          h('td', { classe: 'num' }, euros(p.realise)),
          h('td', { classe: 'num' }, euros(p.budgete ?? 0)),
        );
      }),
    ),
  );

  // ── Les exercices suivants ──────────────────────────────────────────────
  const anneesSuivantes = annees.filter((x) => x > an);
  const scenariosAffiches = d.trajectoires.filter((t) => t.cle !== 'sim');

  const tableProjections = h(
    'div',
    { classe: 'enveloppe-table' },
    h(
      'table',
      { classe: 'table-budget' },
      h('caption', { classe: 'invisible' }, 'Réserve projetée par exercice et par scénario'),
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { scope: 'col' }, 'Exercice'),
          h('th', { scope: 'col' }, 'Seuil d’alerte'),
          ...scenariosAffiches.map((t) => h('th', { scope: 'col' }, t.libelle)),
          h('th', { scope: 'col' }, ''),
        ),
      ),
      h(
        'tbody',
        {},
        ...anneesSuivantes.map((annee) => {
          const l = d.veille.lignes.find((x) => x.exercice === annee);
          return h(
            'tr',
            { classe: (l?.nbSousSeuil ?? 0) > 0 ? 'ligne-alerte' : '' },
            h('th', { scope: 'row' }, String(annee)),
            h('td', {}, euros(l?.seuil ?? 0)),
            ...scenariosAffiches.map((t) => {
              const c = l?.cellules.find((x) => x.scenario === t.cle);
              return h(
                'td',
                {},
                euros(c?.reserve ?? 0),
                c?.sousSeuil === true
                  ? h('span', { classe: 'badge b-danger' }, ' sous le seuil')
                  : c?.serre === true
                    ? h('span', { classe: 'badge b-amber' }, ' serré')
                    : null,
              );
            }),
            h(
              'td',
              {},
              h(
                'button',
                {
                  classe: 'btn-action',
                  type: 'button',
                  'aria-label': `Comparer les scénarios sur l’exercice ${annee}`,
                  surClick: () => {
                    demanderExercice(annee);
                    allerA('exercice-compare');
                  },
                },
                'Comparer',
              ),
            ),
          );
        }),
      ),
    ),
  );

  const masse = d.paie?.exerciceCourant;

  return h(
    'div',
    { classe: 'page', id: 'page-tableau-bord', role: 'region', 'aria-label': 'Tableau de bord' },
    h(
      'div',
      { classe: 'entete-page' },
      h(
        'div',
        {},
        h('h1', {}, `Pilotage de l’exercice ${an}`),
        h(
          'p',
          { classe: 'chapeau' },
          `Comptabilité tenue en encaissements et décaissements : les montants constatés sont des ` +
            `flux de trésorerie, non des charges rattachées.`,
        ),
      ),
      h(
        'div',
        { classe: 'reperes' },
        h('span', {}, `Arrêté au ${dateFr(d.meta.dateArrete)}`),
        h('span', {}, d.meta.versionPrevisionnel),
      ),
    ),

    // ── L'essentiel, en tête ────────────────────────────────────────────
    //
    // Un membre du Conseil national qui ouvre cette page doit voir en dix
    // secondes où en est l'exercice et si la trajectoire tient. Les alertes
    // viennent après : elles détaillent, elles n'introduisent pas.
    h(
      'section',
      {},
      verdict,
      h(
        'div',
        { classe: 'barre-actions' },
        h('h2', { classe: 'titre' }, `L’exercice ${an} au ${dateFr(d.meta.dateArrete)}`),
        h(
          'button',
          { classe: 'btn-action', type: 'button', surClick: () => { allerA('exercice'); } },
          'Détail de l’exécution',
        ),
      ),
      h(
        'div',
        { classe: 'grille-bord' },
        tuile(
          'Encaissements',
          keurosValeur(ex.encaissements),
          'k€',
          `sur ${euros(pr.produitsVotes)} de produits ${d.previsionnelVote.reconduit ? 'reconduits' : 'votés'} · ` +
            `${euros(pr.resteARecevoir)} restent à recevoir` +
            (pr.sourceCotisations === 'estimation' ? ' (cotisations selon l’estimation de fin de campagne)' : '') +
            (pr.versementsNotifies.length > 0 ? ', dont des versements notifiés' : ''),
          {
            part: d.tauxExecution.produits,
            teinte: teinteRythme,
            gauche: pourcent(d.tauxExecution.produits, 0),
            droite: euros(pr.produitsVotes),
          },
        ),
        tuile(
          'Décaissements rattachés',
          keurosValeur(ex.decaissementsRattaches),
          'k€',
          `sur ${euros(pr.chargesVotees)} de charges votées · ${euros(ex.decaissementsAutresExercices)} rattachés à un autre exercice`,
          {
            part: d.tauxExecution.charges,
            teinte: 'color-slate',
            gauche: pourcent(d.tauxExecution.charges, 0),
            droite: euros(pr.chargesVotees),
          },
        ),
        tuile(
          'Résultat projeté au 31 décembre',
          keurosValeur(pr.resultatProjete),
          'k€',
          `contre ${eurosSigne(pr.resultatVote)} au prévisionnel, soit ${eurosSigne(pr.ecartResultat)}`,
        ),
        tuile(
          `Réserve attendue fin ${an}`,
          keurosValeur(ch.reserveAttendue),
          'k€',
          `${nombre(ch.multipleSeuil, 2)} fois le seuil d’alerte de ${euros(ch.seuilSecurite)}`,
          {
            part: Math.min(1, ch.multipleSeuil / 3),
            teinte: ch.multipleSeuil >= 2 ? 'color-green' : ch.multipleSeuil >= 1.5 ? 'color-amber' : 'color-danger',
            gauche: `${nombre(ch.multipleSeuil, 2)} × le seuil`,
            droite: euros(ch.seuilSecurite),
          },
        ),
      ),

      // Les ressources, poste par poste : c'est la lecture de l'écran
      // « Exécution », et c'est ce qui explique le taux d'encaissement.
      regleAccent('Ce qui est encaissé, ressource par ressource'),
      barresRessources,
      noteLecture(
        `Comptabilité tenue en encaissements et décaissements : les montants ci-dessus sont des flux de ` +
          `trésorerie, non des charges et produits rattachés. ` +
          `${entier(d.adherents.avancement.appels)} cotisations sont ouvertes sur l'exercice et ` +
          `${entier(d.adherents.avancement.facturesEmises)} factures émises, contre ` +
          `${entier(d.adherents.avancement.facturesPrecedentes)} à la même date un an plus tôt.`,
      ),
    ),

    // ── Les exercices suivants ──────────────────────────────────────────
    h(
      'section',
      {},
      h(
        'div',
        { classe: 'barre-actions' },
        h('h2', { classe: 'titre' }, `Ce que disent les scénarios pour ${anneesSuivantes[0] ?? ''}-${anneesSuivantes[anneesSuivantes.length - 1] ?? ''}`),
        h(
          'button',
          { classe: 'btn-action', type: 'button', surClick: () => { allerA('exercice-compare'); } },
          'Comparer les scénarios année par année',
        ),
        h(
          'button',
          { classe: 'btn-action', type: 'button', surClick: () => { allerA('veille'); } },
          'Veille des réserves',
        ),
      ),
      tableProjections,
      noteLecture(
        `Réserve à la clôture de chaque exercice, sous chaque scénario, confrontée au seuil d'alerte — le ` +
          `plancher de trésorerie augmenté du coût de rupture du salarié le plus cher. Cliquer sur un exercice ` +
          `ouvre la comparaison poste par poste de cette année-là.`,
      ),
    ),

    // ── Ce qui ne va pas ────────────────────────────────────────────────
    h(
      'section',
      {},
      h(
        'div',
        { classe: 'barre-actions' },
        h('h2', { classe: 'titre' }, 'Ce qui appelle une décision'),
        h(
          'button',
          { classe: 'btn-action', type: 'button', surClick: () => { allerA('alertes'); } },
          'Voir toutes les règles',
        ),
      ),
      listeAlertes(d.alertes, ['alerte', 'vigilance']),
    ),

    // ── Masse salariale ─────────────────────────────────────────────────
    masse
      ? h(
          'section',
          {},
          h(
            'div',
            { classe: 'barre-actions' },
            h('h2', { classe: 'titre' }, 'Masse salariale'),
            h(
              'button',
              { classe: 'btn-action', type: 'button', surClick: () => { allerA('masse-salariale'); } },
              'Détail par salarié',
            ),
          ),
          h(
            'div',
            { classe: 'grille-bord' },
            tuile(
              'Masse chargée projetée',
              keurosValeur(masse.masseChargee),
              'k€',
              `${euros(masse.brut)} de brut, ${euros(masse.chargesPatronales)} de charges patronales` +
                (masse.titresRestaurant > 0 ? `, ${euros(masse.titresRestaurant)} de titres-restaurant` : ''),
              {
                part:
                  d.previsionnelVote.charges.personnel > 0
                    ? masse.masseChargee / d.previsionnelVote.charges.personnel
                    : 0,
                teinte: masse.masseChargee <= d.previsionnelVote.charges.personnel ? 'color-green' : 'color-danger',
                gauche: pourcent(
                  d.previsionnelVote.charges.personnel > 0
                    ? masse.masseChargee / d.previsionnelVote.charges.personnel
                    : 0,
                  0,
                ),
                droite: `budgété ${euros(d.previsionnelVote.charges.personnel)}`,
              },
            ),
            tuile(
              'Taux de charges patronales',
              pourcent(masse.taux, 1),
              null,
              masse.tauxConstate
                ? 'constaté sur les écritures de paie'
                : 'estimé — aucune écriture de paie exploitable',
            ),
            tuile(
              'Effectif',
              entier(masse.effectif),
              masse.effectif > 1 ? 'salariés' : 'salarié',
              `part de la masse dans les charges projetées : ${pourcent(pr.chargesProjetees > 0 ? masse.masseChargee / pr.chargesProjetees : 0, 0)}`,
            ),
          ),
        )
      : null,

    // ── Où va la trajectoire ────────────────────────────────────────────
    h(
      'section',
      {},
      h(
        'div',
        { classe: 'barre-actions' },
        h('h2', { classe: 'titre' }, 'Où va la trajectoire'),
        h(
          'button',
          { classe: 'btn-action', type: 'button', surClick: () => { allerA('trajectoires'); } },
          'Comparer les scénarios',
        ),
      ),
      graphique,
      noteLecture(
        sc3
          ? `Hypothèse de référence — ${sc3.libelle} : réserve de ${euros(sc3.reserveFinale)} fin ` +
              `${annees[annees.length - 1] ?? ''}, cumul de ${eurosSigne(sc3.resultatCumule)}, ` +
              (sc3.retourEquilibre
                ? `retour à l’équilibre en ${sc3.retourEquilibre}.`
                : 'aucun retour à l’équilibre sur la période.')
          : '',
      ),
    ),

    // ── Trésorerie ──────────────────────────────────────────────────────
    h(
      'section',
      {},
      h(
        'div',
        { classe: 'barre-actions' },
        h('h2', { classe: 'titre' }, 'Trésorerie'),
        h(
          'button',
          { classe: 'btn-action', type: 'button', surClick: () => { allerA('tresorerie'); } },
          'Trésorerie et besoin en fonds de roulement',
        ),
      ),
      graphiqueTresorerie,
      noteLecture(
        `Solde bancaire mois par mois depuis ${euros(d.tresorerie.soldeOuverture)} au 1ᵉʳ janvier. ` +
          `La bande rouge est le plancher de trésorerie retenu par la direction. ` +
          `Le besoin en fonds de roulement, présenté sur l'écran dédié, est minoré : aucune source ne porte ` +
          `les dettes fournisseurs.`,
      ),
    ),

    // ── Structure des charges ───────────────────────────────────────────
    h(
      'section',
      {},
      h(
        'div',
        { classe: 'barre-actions' },
        h('h2', { classe: 'titre' }, 'Où va l’argent'),
        h(
          'button',
          { classe: 'btn-action', type: 'button', surClick: () => { allerA('exercice'); } },
          'Détail de l’exécution',
        ),
      ),
      graphiqueAnneau(d.repartitions.chargesProjetees.parts, {
        titre: `Répartition des charges projetées ${an}`,
        total: d.repartitions.chargesProjetees.total,
        sousTitre: `charges ${an}`,
        enteteAbscisse: 'Catégorie',
      }),
      noteLecture(
        `Charges du budget de référence, réparties par catégorie de la nomenclature. La part du personnel ` +
          `commande tout arbitrage : à ressources inchangées, elle est la seule enveloppe dont le déplacement ` +
          `change l'ordre de grandeur du résultat.`,
      ),
    ),

    // ── Adhérents ───────────────────────────────────────────────────────
    h(
      'section',
      {},
      h(
        'div',
        { classe: 'barre-actions' },
        h('h2', { classe: 'titre' }, 'Adhérents'),
        h(
          'button',
          { classe: 'btn-action', type: 'button', surClick: () => { allerA('adherents'); } },
          'Effectifs et recouvrement',
        ),
      ),
      h(
        'div',
        { classe: 'grille-bord' },
        tuile(
          'Cotisations appelées',
          entier(d.adherents.appelsExerciceCourant),
          null,
          `${entier(d.adherents.serieCourante?.payeurs ?? 0)} acquittées à ce jour, ` +
            `${entier(d.adherents.payeursProjetes)} payeurs projetés en fin d’exercice`,
          {
            part:
              d.adherents.appelsExerciceCourant > 0
                ? (d.adherents.serieCourante?.payeurs ?? 0) / d.adherents.appelsExerciceCourant
                : 0,
            teinte: 'color-amber',
            gauche: pourcent(
              d.adherents.appelsExerciceCourant > 0
                ? (d.adherents.serieCourante?.payeurs ?? 0) / d.adherents.appelsExerciceCourant
                : 0,
              0,
            ),
            droite: `taux ${d.cloture.exercice} : ${pourcent(d.adherents.tauxRecouvrementPrecedent, 1)}`,
          },
        ),
        tuile(
          'Produit moyen implicite',
          euros(d.adherents.produitMoyenImplicite).replace(' €', ''),
          '€',
          `pente constatée hors exercices atypiques : ${nombre(d.adherents.regression.pente, 2)} € par an ` +
            `(R² ${nombre(d.adherents.regression.r2, 2)})`,
        ),
        tuile(
          'Record historique',
          entier(d.adherents.recordHistorique),
          'payeurs',
          'toute trajectoire qui en vise plus du double est classée hypothèse haute',
        ),
      ),
    ),
  );
}
